import { apiClient } from './api'

interface NotificationPayload {
  player_id: string
  type: 'profile_view' | 'profile_save' | 'message' | 'trial_invitation' | 'verification_change' | 'announcement' | 'general'
  title: string
  message: string
  actor_name?: string
  actor_role?: 'scout' | 'club' | 'admin' | 'player'
  actor_id?: string
  link?: string
  metadata?: Record<string, any>
}

/**
 * Create a player notification.
 * Fire-and-forget: errors are logged but never thrown.
 */
export async function notifyPlayer(payload: NotificationPayload): Promise<void> {
  try {
    await apiClient.createPlayerNotification(payload)
  } catch (err) {
    console.error('Failed to create notification:', err)
  }
}

/** Profile viewed by a scout or club */
export function notifyProfileViewed(
  playerId: string,
  viewerName: string,
  viewerRole: 'scout' | 'club' | 'admin' | 'player',
  viewerId?: string
) {
  return notifyPlayer({
    player_id: playerId,
    type: 'profile_view',
    title: 'Profile Viewed',
    message: `${viewerName} viewed your profile.`,
    actor_name: viewerName,
    actor_role: viewerRole,
    actor_id: viewerId,
  })
}

/** Profile saved / added to watchlist */
export function notifyProfileSaved(
  playerId: string,
  saverName: string,
  saverRole: 'scout' | 'club' | 'admin',
  saverId?: string
) {
  return notifyPlayer({
    player_id: playerId,
    type: 'profile_save',
    title: 'Profile Saved',
    message: `${saverName} saved your profile to their watchlist.`,
    actor_name: saverName,
    actor_role: saverRole,
    actor_id: saverId,
  })
}

/** Message received */
export function notifyMessageReceived(
  playerId: string,
  senderName: string,
  senderRole: 'scout' | 'club' | 'admin',
  senderId?: string
) {
  return notifyPlayer({
    player_id: playerId,
    type: 'message',
    title: 'New Message',
    message: `You received a new message from ${senderName}.`,
    actor_name: senderName,
    actor_role: senderRole,
    actor_id: senderId,
  })
}

/** Trial invitation received */
export function notifyTrialInvitation(
  playerId: string,
  clubName: string,
  trialDate: string,
  venue: string
) {
  return notifyPlayer({
    player_id: playerId,
    type: 'trial_invitation',
    title: 'Trial Invitation',
    message: `${clubName} invited you to a trial on ${new Date(trialDate).toLocaleDateString()} at ${venue}.`,
    metadata: { club_name: clubName, trial_date: trialDate, venue },
  })
}

/** Verification status changed */
export function notifyVerificationChange(
  playerId: string,
  status: 'verified' | 'unverified',
  verifierName?: string
) {
  const verb = status === 'verified' ? 'verified' : 'unverified'
  const actor = verifierName ? ` by ${verifierName}` : ''
  return notifyPlayer({
    player_id: playerId,
    type: 'verification_change',
    title: 'Verification Update',
    message: `Your profile has been ${verb}${actor}.`,
    actor_name: verifierName,
  })
}
